/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-11-25
 */

import { useMemo } from 'react';
import { useGetUserStudiosQuery } from '../features/studios/studiosApi';

/**
 * Studio role type
 */
type StudioRole = 'owner' | 'manager' | 'admin' | 'instructor';

/**
 * Studio with tool access information
 */
export interface StudioWithToolAccess {
  id: string;
  name: string;
  role: StudioRole;
  hasAccess: boolean;
}

/**
 * Hook to manage studio tool access for the current user.
 * Supports flexible permission requirements (owner/manager or owner/manager/admin).
 *
 * @param requiredRoles - Array of roles that have access to the tool
 * @returns Object containing eligible studios and access information
 */
export const useToolAccess = (requiredRoles: StudioRole[]) => {
  // Get user's studios with role information
  const { data: studiosData, isLoading, error } = useGetUserStudiosQuery();

  const {
    eligibleStudios,
    hasToolAccess,
    hasSingleStudio,
    hasMultipleStudios,
  } = useMemo(() => {
    if (!studiosData?.studios) {
      return {
        eligibleStudios: [],
        hasToolAccess: false,
        hasSingleStudio: false,
        hasMultipleStudios: false,
      };
    }

    // Filter studios where user has one of the required roles
    const toolEligibleStudios = studiosData.studios
      .filter(studio => {
        // Get the user role from the API response
        const userRole = (studio as any).user_role as StudioRole;
        // Check if user's role is in the required roles list
        return requiredRoles.includes(userRole);
      })
      .map(studio => ({
        id: studio.id,
        name: studio.name,
        role: (studio as any).user_role as StudioRole,
        hasAccess: true,
      }));

    const result = {
      eligibleStudios: toolEligibleStudios,
      hasToolAccess: toolEligibleStudios.length > 0,
      hasSingleStudio: toolEligibleStudios.length === 1,
      hasMultipleStudios: toolEligibleStudios.length > 1,
    };

    return result;
  }, [studiosData, requiredRoles]);

  return {
    eligibleStudios,
    isLoading,
    error,
    hasToolAccess,
    hasSingleStudio,
    hasMultipleStudios,
  };
};

/**
 * Preset hook for owner/manager only tools
 * (Team Bios, Announcements, Birthday Widget, Scheduling, Financial Insights)
 */
export const useOwnerManagerAccess = () => useToolAccess(['owner', 'manager']);

/**
 * Preset hook for owner/manager/admin tools
 * (Students, QuickBooks)
 */
export const useOwnerManagerAdminAccess = () =>
  useToolAccess(['owner', 'manager', 'admin']);
